<template>
  <section
    aria-label="Interactive campus map"
    class="r-cover transition"
    :class="{
      'lg:pl-144': !isTour && categoriesOpen && !isLocation,
      'lg:pl-78': !isTour && isLocation
    }"
  >
    <div
      :class="[slideoutOpen ? 'transform-translate-x-0-y--1/2' : '-transform-translate-x-full-y--1/2']"
      class="absolute z-20 left-0 top-1/2 w-64 md:w-80 max-w-70% transition-transform duration-500 ease-in-out"
    >
      <button
        id="slideout-modal-toggle"
        title="Toggle slideout menu"
        aria-label="Toggle slideout menu"
        tabindex=""
        class="slideout-modal-open absolute left-full top-1/2 transform-translate-x-0-y--1/2 px-2 py-3 rounded-r-0 bg-white focus:bg-black lg:hover:bg-accent-1 fill-accent-1 focus:fill-white lg:hover:fill-white transition-colors duration-500 ease-in-out focus:outline-none"
        :class="{ 'animate-arrow': lteTablet }"
        @click="toggleSlideout"
      >
        <div class="flex-shrink-0 pointer-events-none select-none w-3 h-5 transition-transform duration-1000 ease-in-out">
          <SvgIcon
            class="w-3 h-5 transition-transform duration-1000 ease-in-out"
            :class="{ 'transform-rotate-y-180': !slideoutOpen }"
            icon="chevron-left"
          />
        </div>
      </button>
      <div class="r-cover overflow-hidden rounded-r-0 bg-white" :inert="!slideoutOpen">
        <div class="relative w-full max-h-full overflow-auto scrolling-touch" style="max-height: 760px;">
          <header class="px-6 py-5 bg-off-white">
            <h1 class="mb-1 last:mb-0 text-body text-lg md:text-xl leading-thin font-body font-body-normal normal-case">
              Just Explore
            </h1>
          </header>
          <div class="p-4">
            <ul>
              <li
                class="mb-0_5 last:mb-0"
                v-for="(item, i1) in mapMarkers"
                :key="i1"
              >
                <button
                  :class="[
                    'w-full slideout-link slideout-link-navspot group p-2 flex items-center ',
                    'bg-white text-body focus:bg-accent-2 focus:outline-none hover:bg-accent-2'
                  ]"
                  :title="item.title"
                  @click="sidebarItemClick(item.slug, item.lat, item.lng)"
                >
                  <span class="w-full text-body normal-case">
                    {{ htmlDecode(item.title) }}
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div
      id="map"
      ref="Map"
      class="rv-map__leaflet h-full w-full z-0 bg-off-white"
      aria-label="Map with location"
    />

    <div
      v-if="showLatLngDisplay"
      class="absolute bottom-8 right-20 p-3 bg-white text-sm"
    >
      <span class="block">Latitude: {{ Math.round(dblClickCoords.x) }}</span>
      <span class="block">Longitude: {{ Math.round(dblClickCoords.y) }}</span>
    </div>

    <BtnsMap
      v-if="!showTourMap"
      class="absolute bottom-20 md:bottom-10 right-3 md:right-6"
      :min-zoom="minZoom"
      :max-zoom="maxZoom"
      :current-zoom="currentZoom"
      @mapCenter="mapCenter"
      @mapZoomIn="mapZoomIn"
      @mapZoomOut="mapZoomOut"
    />
    <div class="bg-white py-1 px-2 absolute bottom-0 right-0">
      <a class="cursor-pointer text-sm font-sm" href="https://leafletjs.com" title="A JS library for interactive maps" target="_blank">
        Leaflet
      </a>
    </div>
  </section>
</template>

<script>
/* global L: false */
import { mapState, mapGetters } from 'vuex'
import BtnsMap from '@/components/buttons/BtnsMap.vue'
import SvgIcon from '@/components/SvgIcon.vue'

import 'leaflet/dist/leaflet.css'
import 'leaflet/dist/leaflet.js'
import 'leaflet-rastercoords/rastercoords.js'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import 'leaflet.markercluster/dist/leaflet.markercluster.js'

export default {
  name: 'Map',

  components: {
    BtnsMap,
    SvgIcon
  },

  data() {
    return {
      publicPath: process.env.BASE_URL,
      map: '',
      img: [5472, 2843],
      minZoom: -1,
      maxZoom: -1,
      landmarkLayer: '',
      markerLayer: '',
      markersList: {},
      dblClickCoords: '',
      showLatLngDisplay: false,
      currentZoom: 3,
      slideoutOpen: true,
    }
  },

  computed: {
    ...mapState([
      'lteTablet',
      'showTourMap',
      'isTour',
      'categoriesOpen',
      'isLocation'
    ]),
    ...mapGetters(['level', 'post', 'locations', 'getTourGroupBySlug']),

    mapMarkers() {
      return this.locations
    }
  },

  watch: {
    'category.slug': {
      immediate: true,
      handler() {
        setTimeout(() => {
          if (this.category) {
            this.addMapMarkers()
          }
        }, 400)
      }
    },

    categoriesOpen: {
      immediate: false,
      handler() {
        setTimeout(() => {
          this.map.invalidateSize(true)
        }, 300)
      }
    },

    isLocation: {
      immediate: false,
      handler() {
        setTimeout(() => {
          this.map.invalidateSize(true)
        }, 300)
      }
    }
  },

  mounted() {
    this.initMap()
    this.addMapMarkers()
    document.onkeyup = e => {
      if (e.ctrlKey && e.altKey && e.shiftKey && e.which === 85) {
        this.showLatLngDisplay = !this.showLatLngDisplay
      }
    }
    if(this.lteTablet) {
      this.slideoutOpen = false
    }
  },

  methods: {
    initMap() {
      // Original image dimensions
      this.minZoom = this.lteTablet ? 3 : 3
      this.maxZoom = this.lteTablet ? 4 : 5
      const tiles = 'tiles_5472x2843'

      // Create the map
      this.map = L.map('map', {
        minZoom: this.minZoom,
        maxZoom: this.maxZoom,
        zoomControl: false,
        layerControl: false
      })

      // Assign map and image dimensions
      this.rc = new L.RasterCoords(this.map, this.img)
      // Set max zoom Level (might be `x` if gdal2tiles was called with `-z 0-x` option)
      // this.map.setMaxZoom(this.rc.zoomLevel(6))
      // Set the view in the middle of the image
      this.map.setView(this.rc.unproject([this.img[0] / 2, this.img[1] / 2]), 3)
      // Define layer group for markers
      this.layerGroup = L.featureGroup([])
      // The tile layer containing the image generated with gdal2tiles --leaflet ...
      L.tileLayer(`${this.publicPath}uploads/${tiles}/{z}/{x}/{y}.png`, {
        noWrap: true
      }).addTo(this.map)

      // Just the utility to get coords
      this.map.on('dblclick', event => {
        this.dblClickCoords = this.rc.project(event.latlng)
      })

      this.map.on('zoomend', () => {
        this.currentZoom = this.map.getZoom()
      })
    },
    addMapMarkers() {
      if (this.markerLayer) {
        this.map.removeLayer(this.markerLayer)
        this.markersList = {}
      }

      this.markerLayer = L.markerClusterGroup({
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        maxClusterRadius: 80,
        spiderfyDistanceMultiplier: 2,
        removeOutsideVisibleBounds: false,
        spiderLegPolylineOptions: {
          opacity: 0
        },
        zoomToBoundsOnClick: true
      }).addTo(this.map)

      this.mapMarkers.forEach(item => {
        const img = {
          src: item?.thumbnail?.sizes?.thumbnail,
          alt: item?.thumbnail?.alt
        }

        const farRight = (item.lat / this.img[0]) >= 0.6
        const popupXAnchor = farRight ? 'right-0' : 'left-0'
        const popupLargePadding = farRight ? 'pr' : 'pl'
        const popupSmallPadding = farRight ? 'pl' : 'pr'

        const landmarkMarker = `
          <button aria-label="${this.htmlDecode(item.title)}" class="marker-${this.htmlDecode(item.slug)} landmark relative group h-16 w-16 rounded-full focus-a text-base font-sans">
            <div class="landmark-popup marker-popup-${this.htmlDecode(item.slug)} absolute ${popupXAnchor} top-0 h-full ${popupLargePadding}-20 ${popupSmallPadding}-8 rounded-full bg-white opacity-0 hidden transition">
              <div>
                <p class="font-bold leading-thin">${this.htmlDecode(item.title)}</p>
                <p class="max-w-380 font-light text-sm leading-none">${this.htmlDecode(item.landmark_info)}</p>
              </div>
            </div>
            <div class="leaflet-marker-wrap relative h-full w-full rounded-full border border-white">
              <img class="leaflet-marker-img" alt="${this.htmlDecode(img.alt || item.title)}" src="${img.src}" class="h-full w-full rounded-full">
            </div>
          </button>
        `
        const locationMarker = `
          <button aria-label="${this.htmlDecode(item.title)}" class="marker-${this.htmlDecode(item.slug)} relative group h-12 w-12 rounded-full focus-a text-base font-sans">
            <div class="location marker-popup-${this.htmlDecode(item.slug)} absolute ${popupXAnchor} top-0 h-16 ${popupLargePadding}-12 ${popupSmallPadding}-8 rounded-full bg-white opacity-0 hidden transition">
              <div>
                <p class="leading-thin">${this.htmlDecode(item.title)}</p>
              </div>
            </div>
            <div class="relative h-full w-full">
              <img class="leaflet-marker-img h-12 w-12 mt-3" alt="${this.htmlDecode(item.title)}" src="${require('@/assets/icon-marker.svg')}" class="h-full w-full rounded-full">
            </div>
          </button>
        `

        const markerIcon = L.divIcon({
          html: item.location_type === 'landmark_location' ? landmarkMarker : locationMarker
        })

        const marker =
          L.marker(
            this.rc.unproject([item.lat, item.lng]),
            {
              icon: markerIcon,
              keyboard: false
            }
          )

        // Add to map
        marker.addTo(this.markerLayer)
        // Add to list
        this.markersList[`marker-${item.slug}`] = marker

        const latLng = this.rc.project(marker.getLatLng())
        const offsetLatLng = { x: latLng.x, y: latLng.y }

        L.DomEvent.addListener(marker, 'click', () => {
          if (item.location_type !== 'landmark_location') {
          this.map.setView(this.rc.unproject(offsetLatLng), 5, {
            animate: true,
            pan: { duration: 1 }
          })
            // Build link dependent on if single 360 or group
            const group = this.getTourGroupBySlug(item.linked_panorama.post_name)
            const first360 = group ? group.children[0].slug : null
            const link = first360
            ? `/just-explore/${item.linked_panorama.post_name}/${first360}`
            : `/just-explore/single/${item.linked_panorama.post_name}`
            this.$router.push(link)
          }
        })

        L.DomEvent.addListener(marker, 'mouseenter', () => {
          this.closeAllPopups()
          this.showHotSpotPopup(item.slug)
        })

        L.DomEvent.addListener(marker, 'mouseleave', () => {
          this.hideHotSpotPopup(item.slug)
        })
      })

      if (this.mapMarkers.length) {
        this.map.fitBounds(
          this.markerLayer.getBounds(),
          { animate: true, duration: 0.5 },
          { padding: [50, 50] }
        )
      }
    },
    // Map pins
    closeAllPopups() {
      this.locations.forEach((item) => {
        const popup = document.querySelector(`.marker-popup-${item.slug}`)
        if(popup) {
          popup.classList.remove('opacity-100', 'pointer-events-auto')
        }
      })
    },
    showHotSpotPopup(slug) {
      const popup = document.querySelector(`.marker-popup-${slug}`)
      if (!popup) { return }
      const markerParent = popup.closest('.leaflet-marker-icon')
      markerParent.classList.add('z-important')
      popup.classList.remove('hidden')
      popup.classList.add('opacity-100', 'flex', 'pointer-events-auto')
    },
    hideHotSpotPopup(slug) {
      const popup = document.querySelector(`.marker-popup-${slug}`)
      if (!popup) { return }
      const markerParent = popup.closest('.leaflet-marker-icon')
      markerParent.classList.remove('z-important')
      popup.classList.remove('opacity-100', 'flex', 'pointer-events-auto')
      popup.classList.add('hidden')
    },
    // Sidebar
    sidebarItemClick(slug, lat, lng) {
      const marker = this.markersList[`marker-${slug}`]
      if (marker) {
        this.closeAllPopups()
        this.markerLayer.zoomToShowLayer(marker, () => {
          this.map.setView(this.rc.unproject([lat, lng]))
          this.showHotSpotPopup(slug)
        })
      }
    },
    toggleSlideout() {
      if (this.slideoutOpen) {
        this.slideoutOpen = false
      } else {
        this.slideoutOpen = true
      }
    },
    // Map controls
    mapCenter() {
      this.map.setView(
        this.rc.unproject([this.img[0] / 2, this.img[1] / 2]),
        3,
        {
          animate: true,
          pan: { duration: 2 }
        }
      )
    },
    mapZoomIn() {
      this.map.zoomIn()
    },
    mapZoomOut() {
      this.map.zoomOut()
    }
  }
}
</script>

<style lang="scss">
/*
pl-20 pr-20
pl-12 pr-12
pl-8 pr-8
*/
#no-touch .rv-map__leaflet:focus:after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  pointer-events: none;
  user-select: none;
  border: 3px solid var(--color-focus);
  z-index: 500;
}

.leaflet-container {
  /* background-color:rgba(187,226,207,1); */
  background-color: #ededed;
}

.leaflet-control-attribution.leaflet-control {
  display: none;
}

.leaflet-marker-icon {
  border-radius: 1000px;
}

.leaflet-div-icon {
  background: none;
  border: none;
}

.leaflet-marker-icon:focus {
  // outline: none;
  border: 3px solid var(--color-focus);
}

.leaflet-popup-tip {
  background: var(--color-primary);
}

.leaflet-popup-content {
  /* background: transparent; */
  margin: 0;
}

.leaflet-popup-content-wrapper {
  background: transparent;
  border-radius: 0;
  padding: 0;
}

.map__marker-link {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

.marker-cluster {
  background-color: rgba(3, 124, 194, 0.5);

  &:focus {
    outline: 2px solid #fff!important;
  }
}

.marker-cluster div {
  background-color: #037CC2;
}

.marker-cluster span {
  color: white;
}

.leaflet-marker-wrap {
  border-radius: 100%;
  overflow: hidden;
}

.leaflet-marker-img {
  width: 100%;
}

.location,
.landmark-popup {
  align-items: center;
  width: max-content;
}

.slideout-link {
  &:hover,
  &:focus {
    span {
      color: #ffffff;
    }
  }
}

.z-important {
  z-index: 999999999 !important;
}

.transform-rotate-y-180 {
  -webkit-transform: rotate(-180deg);
}

.animate-arrow {
  animation: animateArrow 1s linear 2s 4 alternate;

  path {
    animation: animateArrowBackground 1s linear 2s 4 alternate;
  }
}

@keyframes animateArrowBackground {
  from {
    fill: #000000;
  }

  to {
    fill: #ffffff;
  }
}

@keyframes animateArrow {
  from {
    background: #ffffff;
  }

  to {
    background: #000000;
  }
}
</style>
